// Envelope delivery that survives a backend restart.
//
// Sentry's browser transport posts an envelope exactly once: a refused
// connection or a 5xx is an answer it accepts, so the report is gone (the SDK
// records a client outcome, but the event never arrives). This app always has a
// window where the tunnel cannot answer — the dev supervisor restarts the
// backend on its own port (scripts/nidus-dev.mjs), and a deploy restarts it in
// production — so a report whose send coincides with that window would be
// dropped. That is not the tunnel's design failing; it is the transport's
// one-shot policy, which this replaces.
//
// Retries reuse the same envelope, which carries the same event id, so a POST
// that did reach the ingest service and lost its answer is deduplicated there
// rather than reported twice.
import { makeFetchTransport, type BrowserOptions } from "@sentry/vue";

/** The shape `Sentry.init` accepts for `transport`, so this is a drop-in for
 * the SDK's own fetch transport. */
type TransportFactory = NonNullable<BrowserOptions["transport"]>;

// Waits between attempts. The sum (7.5 s) outlasts a rebuild plus the startup
// work the backend does before it listens (main.go: the NL generator init runs
// before ListenAndServe), so a request caught in the gap lands after it.
const RETRY_DELAYS_MS = [500, 1000, 2000, 4000];

/** A transport that retries an unanswered envelope across a bounded outage,
 * then gives up so the SDK can record the loss instead of holding the buffer
 * open forever.
 *
 * Anything at or above 500, and any transport-level failure, means no upstream
 * handled the envelope. Everything else is an answer the SDK must see: 429 in
 * particular drives the rate-limit bookkeeping in @sentry/core, and a 4xx means
 * the envelope itself is unacceptable, so a retry would only repeat it. */
export const retryingTransport: TransportFactory = (options) => {
	const inner = makeFetchTransport(options);
	return {
		send: async (envelope) => {
			for (let attempt = 0; ; attempt += 1) {
				let response;
				try {
					response = await inner.send(envelope);
				} catch {
					response = undefined;
				}
				if (response !== undefined && (response.statusCode ?? 0) < 500)
					return response;
				if (attempt === RETRY_DELAYS_MS.length) {
					// Exhausted: hand the failure back so the SDK records the loss.
					if (response === undefined)
						throw new Error("Sentry transport: envelope not delivered");
					return response;
				}
				await new Promise<void>((resolve) =>
					setTimeout(resolve, RETRY_DELAYS_MS[attempt]),
				);
			}
		},
		flush: (timeout) => inner.flush(timeout),
	};
};
